import { Injectable } from '@angular/core';
import { MessageService } from '@sla-ng/core';
import { FieldType, IField } from '@sla-ng/ui-material';
import { BehaviorSubject, firstValueFrom, Observable, of } from 'rxjs';
import { IMeasureUnit } from '../../../models/measure-unit.model';
import { IReference } from '../../../models/reference.model';
import { MeasureUnitResource } from '../../../resources/measure-unit.resource';
import { ReferenceResource } from '../../../resources/reference.resource';
import { IReferenceSizeState } from './reference-size-state.model';

let _state: IReferenceSizeState = {
    data: [], loading: false,
    measureUnits: {},
    columns: [ 'name', 'measure-unit-name', 'edit-button' ],
    dialogFields: [],
    unitsList: [],
};

@Injectable({
    providedIn: 'root',
})
export class ReferenceSizeFacade {

    protected store$: BehaviorSubject<IReferenceSizeState> = new BehaviorSubject<IReferenceSizeState>(_state);

    constructor(private myReferenceResource: ReferenceResource,
                private myMeasureUnitResource: MeasureUnitResource,
                private myMessageService: MessageService) {
    }

    public getStateSnapshot(): IReferenceSizeState {
        return this.store$.value;
    }

    public async fetchData(): Promise<void> {
        this.setIsloading(true);
        const units: IMeasureUnit[] = await this.myMeasureUnitResource.getAllMeasureUnits().toPromise().catch(() => []);
        this.createMeasureUnitMap(units);
        const data: IReference[] = await this.myReferenceResource.getAllReferences().toPromise().catch(() => []);

        this.updateState({ ..._state, data });
        this.setIsloading(false);
        const dialogFields: IField<unknown>[] = [
            { field: 'name', label: 'app.model.reference-size.bezugsgroesse_bezeichnung.label', type: FieldType.text, required: true },
            {
                field: 'measureId', label: 'app.model.reference-size.masseinheit.label', type: FieldType.select, selectData: {
                    displaySearch: true,
                    labelKeys: [ 'name' ],
                    valueKey: 'id',
                    options: of(units),
                }, required: true,
            },
        ];
        this.updateState({ ..._state, dialogFields });
    }

    public subState(): Observable<IReferenceSizeState> {
        return this.store$.asObservable();
    }

    public async addBezugsgroesse(reference: IReference): Promise<void> {
        const createdReference = await this.myReferenceResource.createReference(reference).toPromise().catch(() => null);
        if (createdReference !== null){
            this.myMessageService.successTrans('app.actions.feedback.saved', 'app.page.reference-size.actions.added.label');
        }
    }

    public async editBezugsgroesse(reference: IReference): Promise<void> {
        const editedReference = await this.myReferenceResource.updateReference(reference).toPromise().catch(() => null);
        if (editedReference !== null) {
            this.myMessageService.successTrans('app.actions.feedback.saved', 'app.page.reference-size.actions.edited.label');
        }
    }

    public clearState(): void {
        this.updateState({
            data: [], loading: false,
            measureUnits: {},
            columns: [ 'name', 'measure-unit-name', 'edit-button' ],
            dialogFields: [],
            unitsList: [],
        });
    }

    public async deleteReference(referenceId: number): Promise<void> {
        this.setIsloading(true);
        try {
            await firstValueFrom(this.myReferenceResource.deleteReference(referenceId));
            this.myMessageService.successTrans('app.actions.feedback.saved', 'app.page.reference-size.actions.deleted.label');
            await this.fetchData();
        } catch (e) {
            console.error(e);
        }
        this.setIsloading(false);
    }

    private updateState(state: IReferenceSizeState): void {
        this.store$.next(_state = state);
    }

    private setIsloading(loading: boolean): void {
        this.updateState({ ..._state, loading });
    }

    private createMeasureUnitMap(measureUnits: IMeasureUnit[]): void {
        const measureUnitsMap: Record<number, IMeasureUnit> = {};
        for (const measureUnit of measureUnits){
            measureUnitsMap[ measureUnit.id ] = measureUnit;
        }
        this.updateState({ ..._state, measureUnits: measureUnitsMap });
    }
}
